import { z } from 'zod';
/**
 * Opaque, prefixed identifiers (CLAUDE.md §2.4): UUIDv7 payload behind a type prefix so
 * ids are self-describing in logs, APIs, and audit events. Branded so an OrgId can never
 * be passed where a CounterpartyId is expected.
 *
 * Id generation happens only at the persistence boundary — deterministic domain logic
 * (engine state computation, rule evaluation) never mints ids (Invariant I1).
 */
export const ID_PREFIXES = {
    org: 'org',
    user: 'usr',
    role: 'rol',
    membership: 'mem',
    apiKey: 'key',
    session: 'ses',
    counterparty: 'cp',
    entity: 'ent',
    asset: 'ast',
    claim: 'clm',
    job: 'job',
    check: 'chk',
    evidence: 'ev',
    record: 'rec',
    certificate: 'cert',
    inspector: 'insp',
    attestation: 'att',
    case: 'case',
    source: 'src',
    auditEvent: 'aud',
    document: 'doc',
    professionalRequest: 'pro',
    requirement: 'req',
    recommendation: 'rcm',
    crmLink: 'crm',
    rfpEvent: 'rfp',
    sourcingLink: 'srcl',
    ledgerEntry: 'led',
    webhook: 'wh',
    notification: 'ntf',
    // Garnet Core
    rule: 'rule',
    ruleVersion: 'rv',
    sourceOfLaw: 'sol',
    coverageCell: 'cov',
    release: 'rel',
    queryLog: 'qry',
    test: 'tst',
};
const idPattern = (prefix) => new RegExp(`^${prefix}_[0-9a-f]{32}$`);
export function idSchema(prefix) {
    return z
        .string()
        .regex(idPattern(prefix), `expected an id of the form ${prefix}_<32 hex chars>`)
        .transform((v) => v);
}
/** UUIDv7-style payload: 48-bit ms timestamp + 80 bits of randomness, hex, no dashes. */
export function mintId(prefix, now = Date.now) {
    const ts = now().toString(16).padStart(12, '0');
    const bytes = new Uint8Array(10);
    globalThis.crypto.getRandomValues(bytes);
    const rand = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${prefix}_${ts}${rand}`;
}
export const orgId = idSchema(ID_PREFIXES.org);
export const userId = idSchema(ID_PREFIXES.user);
export const roleId = idSchema(ID_PREFIXES.role);
export const membershipId = idSchema(ID_PREFIXES.membership);
export const apiKeyId = idSchema(ID_PREFIXES.apiKey);
export const sessionId = idSchema(ID_PREFIXES.session);
export const counterpartyId = idSchema(ID_PREFIXES.counterparty);
export const entityId = idSchema(ID_PREFIXES.entity);
export const assetId = idSchema(ID_PREFIXES.asset);
export const claimId = idSchema(ID_PREFIXES.claim);
export const jobId = idSchema(ID_PREFIXES.job);
export const checkId = idSchema(ID_PREFIXES.check);
export const evidenceId = idSchema(ID_PREFIXES.evidence);
export const recordId = idSchema(ID_PREFIXES.record);
export const certificateId = idSchema(ID_PREFIXES.certificate);
export const inspectorId = idSchema(ID_PREFIXES.inspector);
export const attestationId = idSchema(ID_PREFIXES.attestation);
export const caseId = idSchema(ID_PREFIXES.case);
export const sourceId = idSchema(ID_PREFIXES.source);
export const auditEventId = idSchema(ID_PREFIXES.auditEvent);
export const documentId = idSchema(ID_PREFIXES.document);
export const professionalRequestId = idSchema(ID_PREFIXES.professionalRequest);
export const requirementId = idSchema(ID_PREFIXES.requirement);
export const recommendationId = idSchema(ID_PREFIXES.recommendation);
export const crmLinkId = idSchema(ID_PREFIXES.crmLink);
export const rfpEventId = idSchema(ID_PREFIXES.rfpEvent);
export const sourcingLinkId = idSchema(ID_PREFIXES.sourcingLink);
export const ledgerEntryId = idSchema(ID_PREFIXES.ledgerEntry);
export const webhookId = idSchema(ID_PREFIXES.webhook);
export const notificationId = idSchema(ID_PREFIXES.notification);
export const ruleId = idSchema(ID_PREFIXES.rule);
export const ruleVersionId = idSchema(ID_PREFIXES.ruleVersion);
export const sourceOfLawId = idSchema(ID_PREFIXES.sourceOfLaw);
export const coverageCellId = idSchema(ID_PREFIXES.coverageCell);
export const releaseId = idSchema(ID_PREFIXES.release);
export const queryLogId = idSchema(ID_PREFIXES.queryLog);
//# sourceMappingURL=ids.js.map